// ---------------------------------------------------------------------------
// O que esta pessoa pode ver no app: nome, se organiza eventos, se tem função
// (barraca, caixa, portaria) e se é admin da plataforma. Usado pela barra de
// cima e pelo menu ☰ no celular. Não altera nada no banco — só lê.
// ---------------------------------------------------------------------------
import { useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';

export interface PortariaEvento {
  evento_id: number;
  evento: string;
  inicio_em: string | null;
}

export interface AcessoApp {
  pronto: boolean;
  logado: boolean;
  /** Nome completo (para montar as iniciais). Vazio se não tiver. */
  nome: string;
  primeiroNome: string;
  organiza: boolean;
  temRifas: boolean;
  temBarraca: boolean;
  temCaixa: boolean;
  portariaEventos: PortariaEvento[];
  adminPlataforma: boolean;
}

const VAZIO: AcessoApp = {
  pronto: false,
  logado: false,
  nome: '',
  primeiroNome: '',
  organiza: false,
  temRifas: false,
  temBarraca: false,
  temCaixa: false,
  portariaEventos: [],
  adminPlataforma: false,
};

function primeiroNomeDe(nome: string): string {
  const p = nome.trim().split(/\s+/)[0] || '';
  if (!p) return '';
  return p.charAt(0).toUpperCase() + p.slice(1);
}

export function useAcessoApp(): AcessoApp {
  const [dados, setDados] = useState<AcessoApp>(VAZIO);

  useEffect(() => {
    let ativo = true;

    async function carregar(session: Awaited<ReturnType<typeof supabase.auth.getSession>>['data']['session']) {
      const user = session?.user;
      if (!user) {
        if (ativo) setDados({ ...VAZIO, pronto: true });
        return;
      }

      let nome = '';
      try {
        const { data } = await supabase.from('vw_meu_perfil').select('nome').maybeSingle();
        nome = (data as { nome?: string } | null)?.nome ?? '';
      } catch {
        /* segue para o próximo caminho */
      }
      if (!nome) {
        nome =
          (user.user_metadata?.full_name as string | undefined) ||
          (user.user_metadata?.name as string | undefined) ||
          '';
      }

      const [evs, rf, pts, cx, pt, adm] = await Promise.all([
        supabase.rpc('meus_eventos'),
        supabase.rpc('minhas_rifas'),
        supabase.rpc('loja_meus_pontos'),
        supabase.rpc('caixa_meus_eventos'),
        supabase.rpc('portaria_meus_eventos'),
        supabase.rpc('sou_admin_plataforma'),
      ]);

      if (!ativo) return;
      setDados({
        pronto: true,
        logado: true,
        nome,
        primeiroNome: primeiroNomeDe(nome),
        organiza: ((evs.data as unknown[]) || []).length > 0,
        temRifas: ((rf.data as unknown[]) || []).length > 0,
        temBarraca: ((pts.data as unknown[]) || []).length > 0,
        temCaixa: ((cx.data as unknown[]) || []).length > 0,
        portariaEventos: (pt.data as PortariaEvento[]) || [],
        adminPlataforma: adm.data === true,
      });
    }

    supabase.auth.getSession().then(({ data }) => {
      if (ativo) void carregar(data.session);
    });

    const { data: sub } = supabase.auth.onAuthStateChange((_evento, session) => {
      // Fora do callback: o supabase pede para não chamar o banco dentro dele.
      window.setTimeout(() => {
        if (ativo) void carregar(session);
      }, 0);
    });

    return () => {
      ativo = false;
      sub.subscription.unsubscribe();
    };
  }, []);

  return dados;
}